import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TAROFF_API_URL } from '../Api';

// Add the convertColor utility function
export const convertColor = (colorString) => {
  try {
    // If it's already a string color like "#ff0000" or "red", just return it
    if (typeof colorString === 'string' && !colorString.startsWith('{')) {
      return colorString;
    }
    
    // Parse the JSON string if it's a string
    const colorObj = typeof colorString === 'string' 
      ? JSON.parse(colorString) 
      : colorString;
    
    // Convert to rgb or rgba format
    if (colorObj.hasOwnProperty('alpha') && colorObj.alpha !== 1) {
      return `rgba(${colorObj.red}, ${colorObj.green}, ${colorObj.blue}, ${colorObj.alpha})`;
    } else {
      return `rgb(${colorObj.red}, ${colorObj.green}, ${colorObj.blue})`;
    }
  } catch (error) {
    console.error('Error converting color:', error);
    return '#000000'; // Default to black on error
  }
};

export const PanelBox = ({ className, children }) => {
  return <div className={`${className} panel-box rounded border border-zinc-300 dark:border-gray-600 p-3 bg-white bg-opacity-75 dark:bg-gray-800 dark:bg-opacity-75`} >{children}</div>
}


export const Title = ({ className = '', children, ...props }) => (
  <h1 className={`text-2xl font-bold my-4 text-right ${className}`} {...props}>
    {children}
  </h1>
);

export const Heading = ({ className = '', children, ...props }) => (
  <h2 className={`text-xl font-bold my-4 text-right ${className}`} {...props}>
    {children}
  </h2>
);

export const Icon = ({ className = 'fal fa-square' }) => (
  <i className={`${className}`} />
);

export const Barcode = ({ barcode }) => (
  <img src={`${TAROFF_API_URL}/reports/barcode?value=${barcode}`} />
);

export const TextView = ({ className = '', children, color, backColor, bold, format, ltr }) => {
  if (format == "money") {
    children = children?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "";
  }
  else if (format == "persian-date-normal") {
    children = new Date(children).toLocaleDateString("fa-IR");
  }
  else if (format == "persian-date-time") {
    children = new Date(children).toLocaleDateString("fa-IR", { hour: '2-digit', minute: '2-digit' });
  }
  else if (format == "persian-datetime-normal") {
    children = new Date(children).toLocaleDateString("fa-IR", { hour: '2-digit', minute: '2-digit' });
  }

  let colorValue = null;
  if (color && typeof color === 'object') {
    // {red: 168, blue: 163, alpha: 1, green: 169}
    colorValue = `rgb(${color.red}, ${color.green}, ${color.blue})`;
  }
  else if (color && typeof color === 'string') {
    colorValue = color;
  }

  let backColorValue = null;
  if (backColor && typeof backColor === 'object') {
    backColorValue = `rgb(${backColor.red}, ${backColor.green}, ${backColor.blue})`;
  }
  else if (backColor && typeof backColor === 'string') {
    backColorValue = backColor;
  }

  if (backColorValue && !colorValue) {
    colorValue = 'white';
  }

  return <span className={`inline-block text-sm text-black dark:text-white my-1 p-1 rounded-md ${className}`} style={{ color: colorValue, backgroundColor: backColorValue, fontWeight: bold ? 'bold' : 'normal', direction: ltr ? 'ltr' : 'rtl' }}>
    {children}
  </span>
};

export const Paragraph = ({ className = '', children, ...props }) => (
  <p className={`text-sm my-1 ${className}`} {...props}>
    {children}
  </p>
);

export const Picture = ({ className = '', ...props }) => (
  <img
    className={`max-w-[100px] h-auto rounded-md ${className}`}
    {...props}
  />
);

export const MobileInfoBox = ({ className = '', children, ...props }) => (
  <div
    className={`p-2 border border-gray-200 rounded-md bg-gray-50 text-sm ${className}`}
    {...props}
  >
    {children}
  </div>
);

export const ButtonLink = ({ className = '', children, ...props }) => (
  <button className={`text-sm my-1 ${className}`} {...props}>
    {children}
  </button>
);

export const MonitoringServiceState = ({ title, description, redStateTimeout, yellowStateTimeout, lastSuccess, alert = false }) => {
  const minutesFromLastSuccess = Math.floor((new Date() - new Date(lastSuccess)) / 60000);
  // alert (a live incident, e.g. Post refusing registrations over the account's debt ceiling) turns
  // the card red at once instead of waiting for lastSuccess to go stale.
  const isRed = alert || (lastSuccess && minutesFromLastSuccess > redStateTimeout);
  const isYellow = !alert && lastSuccess && !isRed && minutesFromLastSuccess > yellowStateTimeout;
  const isGreen = !alert && !isRed && !isYellow;
  const classesMapping = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500',
  };
  const titleClass = `my-1 ${isRed ? classesMapping.red : isYellow ? classesMapping.yellow : isGreen ? classesMapping.green : ''}`;
  return (
    <div className={`text-sm my-1 p-2 border border-gray-200 rounded-md bg-gray-50 dark:bg-gray-800 dark:border-gray-600 text-sm`}>
      <div className="text-sm my-1">
        <div className={titleClass}>{title}</div>
        <div className={`text-sm my-1 ${alert ? 'text-red-600 dark:text-red-300 font-bold' : ''}`}><bdi>{description}</bdi></div>
      </div>
      <div className="text-sm my-1">
        <div className="text-xs my-1">آخرین اجرای موفق</div>
        <div className="text-sm my-1">{lastSuccess}</div>

      </div>
    </div>
  );
}

export const SearchBoxChips = ({ className = '', children, ...props }) => (
  <div className={`text-sm my-1 ${className}`} {...props}>
    {children}
  </div>
);

export const Frame = ({ url }) => (
  <iframe src={url} className="w-full h-full" />
);

export const IncreaseCreditForm = ({ className = '', children, ...props }) => (
  <div className={`text-sm my-1 ${className}`} {...props}>
    {children}
  </div>
);


export const Modal = ({ children, onDismiss, width, height }) => (
  <div
    className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex justify-center items-center"
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        onDismiss();
      }
    }}
  >
    <div
      className="relative rounded-md max-w-1/2 max-h-full overflow-y-auto border border-gray-300 dark:border-gray-600 dark:bg-zinc-900 bg-white [&_.panel-box]:bg-transparent [&_.panel-box]:border-none"
      style={{ width: width, height: height }}
    >
      <button onClick={onDismiss} className="absolute top-2 left-2"><i className="fa-solid fa-xmark" /></button>
      {children}
    </div>
  </div>
);



export function StackedBarChart({ data, categories, title }) {
  const chartData = categories.map((c, cidx) => {
    return {
      name: c,
      ...data.reduce((acc, cur, idx) => ({ ...acc, [cur.name]: cur.data[cidx] }), {})
    }

  });
  return <>
    <div>
      <span className="grow fw-bold fs-5">{title}</span>
    </div>
    <div className='ltr'>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          width={500}
          height={300}
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          {data?.map((r, ridx) => <Bar key={r.name} dataKey={r.name} stackId="a" fill={convertColor(r.color)} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </>
}
